import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HistoryIcon from "@mui/icons-material/History";
import InfoIcon from "@mui/icons-material/Info";
import PrintIcon from "@mui/icons-material/Print";
import { posInputFocus } from "../../../../../store/pos/posSlice";
import InputText from "../../../../../components/inputs/InputText";
import { toast } from "react-toastify";
import posApiService, {
  PosStatus,
  PosHistoryItem,
} from "../../../../../services/pos/posApiService";
import { getDecryptedToken } from "../../../../../utils/crypto/cryptoUtils";
import { getCurrencySymbol } from "../../../../../utils/currency/currencyUtils";
import CashManagementPrint from "./CashManagementPrint";
import useSubmitting from "../../../../../hooks/useSubmitting";

const currencyTimeZoneMap: Record<string, string> = {
  $: "America/New_York",
  "¥": "Asia/Tokyo",
  "Rs.": "Asia/Colombo",
  "€": "Europe/Berlin",
  "£": "Europe/London",
  "₹": "Asia/Kolkata",
  A$: "Australia/Sydney",
  C$: "America/Toronto",
};

const tabBtn =
  "inline-flex flex-1 items-center justify-center gap-1.5 border-b-2 px-3 py-3 text-[11px] font-bold uppercase tracking-[0.04em] transition-colors";

const tabIdle =
  "border-transparent bg-transparent text-erp-muted hover:bg-white/70 hover:text-erp-ink";

const tabActive = "border-erp-accent bg-white text-erp-accent";

const btnGhost =
  "inline-flex h-10 items-center justify-center rounded-lg border border-erp-line bg-white px-4 text-[12px] font-bold text-erp-muted transition-colors hover:border-erp-muted hover:text-erp-ink disabled:cursor-not-allowed disabled:opacity-60";

const btnPrimary =
  "inline-flex h-10 items-center justify-center rounded-lg border border-erp-navy bg-erp-navy px-4 text-[12px] font-bold text-white shadow-[0_4px_12px_rgba(11,28,44,0.16)] transition-colors hover:border-erp-navy-mid hover:bg-erp-navy-mid disabled:cursor-not-allowed disabled:opacity-60";

const PosModel = ({
  isCloseMain,
  isOpen,
}: {
  isCloseMain: () => void;
  isOpen: boolean;
}) => {
  const [formData, setFormData] = useState({
    amount: "",
    reason: "",
    operationType: "cashIn",
  });

  const [submitting, wrap] = useSubmitting();
  const [posStatus, setPosStatus] = useState<PosStatus | null>(null);
  const [currentView, setCurrentView] = useState<"form" | "status" | "history">(
    "form",
  );
  const [posHistory, setPosHistory] = useState<PosHistoryItem[]>([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [showClosePopup, setShowClosePopup] = useState(false);
  const [closeSessionData, setCloseSessionData] = useState({
    actualCashAmount: "",
    discrepancyReason: "",
  });
  const [closeSessionLoading, setCloseSessionLoading] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);

  const [currentTime, setCurrentTime] = useState(new Date());

  const currency = getDecryptedToken("currency");

  const timeZone =
    currencyTimeZoneMap[currency as string] ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dispatch = useDispatch();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        amount: "",
        reason: "",
        operationType: "cashIn",
      });
      setCurrentView("form");
    } else {
      loadPosStatus();
    }
  }, [isOpen]);

  const loadPosStatus = async () => {
    setStatusLoading(true);
    try {
      const result = await posApiService.getPosStatus();
      if (result.success) {
        setPosStatus(result.data);
      }
    } catch (error: any) {
      console.error("Error loading POS status:", error);
      toast.error("Failed to load POS status");
    } finally {
      setStatusLoading(false);
    }
  };

  const loadPosHistory = async () => {
    setHistoryLoading(true);
    try {
      const result = await posApiService.getPosHistory();
      if (result.success) {
        setPosHistory(result.data || []);
      }
    } catch (error: any) {
      console.error("Error loading POS history:", error);
      toast.error("Failed to load POS history");
      setPosHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOperationTypeChange = (type: string) => {
    setFormData((prev) => ({
      ...prev,
      operationType: type,
      amount: "",
      reason: "",
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const drawerId = getDecryptedToken("selectedDrawer");

    if (!formData.amount || Number(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      const payload = {
        amount: Number(formData.amount),
        reason: formData?.reason?.trim(),
        drawerId: Number(drawerId),
        sessionId: "sample",
      };

      let result;
      if (formData.operationType === "cashIn") {
        result = await posApiService.cashIn(payload);
      } else {
        result = await posApiService.cashOut(payload);
      }

      if (result.success) {
        toast.success(
          `${
            formData.operationType === "cashIn" ? "Cash In" : "Cash Out"
          } recorded successfully!`,
        );

        setFormData({
          amount: "",
          reason: "",
          operationType: "cashIn",
        });

        await loadPosStatus();

        if (result.data?.newBalance !== undefined) {
          const sym = getCurrencySymbol();
          toast.info(
            `New balance: ${sym}${Number(result.data.newBalance).toFixed(2)}`,
          );
        }
      } else {
        toast.error(result.message || "Operation failed");
      }
    } catch (error: any) {
      console.error("Cash operation error:", error);
      toast.error(error.message || "Failed to record cash operation");
    }
  };

  const handleCloseSession = async () => {
    if (!posStatus?.isOpen) {
      toast.error("No open POS session to close");
      return;
    }

    setShowClosePopup(true);
    setCloseSessionData({
      actualCashAmount: Number(posStatus.currentBalance).toFixed(2),
      discrepancyReason: "",
    });
  };

  const handleCloseSessionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!closeSessionData.actualCashAmount) {
      toast.error("Please enter the actual cash amount");
      return;
    }

    const discrepancy =
      Number(closeSessionData.actualCashAmount) -
      (posStatus?.currentBalance || 0);

    if (discrepancy !== 0 && !closeSessionData.discrepancyReason.trim()) {
      toast.error("Please provide a reason for the cash discrepancy");
      return;
    }

    setCloseSessionLoading(true);

    try {
      const result = await posApiService.closePosSession({
        actualCashAmount: Number(closeSessionData.actualCashAmount),
        discrepancyReason: closeSessionData.discrepancyReason.trim(),
      });

      if (result.success) {
        const data = result.data;

        if (data && data.adjustmentMade) {
          const sym = getCurrencySymbol();
          let adjustmentMessage = "";
          if (data.adjustmentType === "cash_in_adjustment") {
            adjustmentMessage = `Cash In adjusted by +${sym}${Number(data.adjustmentAmount).toFixed(2)} (More cash found than expected)`;
          } else if (data.adjustmentType === "cash_out_adjustment") {
            adjustmentMessage = `Cash Out adjusted by +${sym}${Number(data.adjustmentAmount).toFixed(2)} (Less cash found than expected)`;
          }

          toast.info(adjustmentMessage, { autoClose: 8000 });
        }

        toast.success("POS session closed successfully");

        if (
          data &&
          typeof data.discrepancy === "number" &&
          data.discrepancy !== 0
        ) {
          const sym = getCurrencySymbol();
          toast.warning(
            `Cash discrepancy: ${
              data.discrepancy > 0 ? "+" : ""
            }${sym}${Number(data.discrepancy).toFixed(2)} - Automatically adjusted`,
            { autoClose: 6000 },
          );
        }

        setShowClosePopup(false);
        await loadPosStatus();
      }
    } catch (error: any) {
      console.error("Error closing session:", error);
      toast.error("Failed to close POS session");
    } finally {
      setCloseSessionLoading(false);
    }
  };

  const handleClosePopupInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    setCloseSessionData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      isCloseMain();
    }
  };

  if (!isOpen) return null;

  const currencySymbol = getCurrencySymbol();

  const formatDateTime = (date: Date | string) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return (
      dateObj.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        timeZone,
      }) +
      " " +
      dateObj.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZone,
      })
    );
  };

  const discrepancyValue =
    Number(closeSessionData.actualCashAmount || 0) -
    (posStatus?.currentBalance || 0);

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-erp-navy/55 p-4 backdrop-blur-[3px]"
      onClick={handleOverlayClick}
    >
      <div className="flex max-h-[90vh] w-full max-w-[540px] flex-col overflow-hidden rounded-2xl border border-erp-line bg-white font-inter shadow-[0_24px_60px_rgba(11,28,44,0.28)]">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-gradient-to-r from-erp-navy via-erp-navy-mid to-erp-navy-soft px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-erp-gold/40 bg-erp-gold/20 text-erp-gold">
              <AccountBalanceWalletIcon className="!text-[20px]" />
            </div>
            <div className="min-w-0">
              <h2 className="m-0 text-[16px] font-bold tracking-wide text-white">
                Cash Management
              </h2>
              <p className="m-0 mt-0.5 text-[11px] font-medium text-white/60">
                Cash in, cash out, status and history
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={isCloseMain}
            className="inline-flex rounded-lg border border-white/15 bg-white/10 p-1.5 text-white/85 transition-colors hover:bg-white/20 hover:text-white"
          >
            <CloseIcon className="!text-[18px]" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex shrink-0 border-b border-erp-line bg-erp-surface">
          <button
            type="button"
            className={`${tabBtn} ${currentView === "form" ? tabActive : tabIdle}`}
            onClick={() => setCurrentView("form")}
          >
            <AccountBalanceWalletIcon className="!text-[16px]" />
            Operations
          </button>
          <button
            type="button"
            className={`${tabBtn} ${currentView === "status" ? tabActive : tabIdle}`}
            onClick={() => {
              setCurrentView("status");
              loadPosStatus();
            }}
          >
            <InfoIcon className="!text-[16px]" />
            Status
          </button>
          <button
            type="button"
            className={`${tabBtn} ${currentView === "history" ? tabActive : tabIdle}`}
            onClick={() => {
              setCurrentView("history");
              loadPosHistory();
            }}
          >
            <HistoryIcon className="!text-[16px]" />
            History
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto p-5 [scrollbar-width:thin]">
          {currentView === "form" && (
            <>
              <div className="mb-5 flex gap-1.5 rounded-[10px] border border-erp-line bg-erp-surface p-1">
                <button
                  type="button"
                  className={[
                    "inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-[13px] font-semibold transition-all",
                    formData.operationType === "cashIn"
                      ? "border border-erp-line bg-white text-erp-ink shadow-[0_2px_8px_rgba(11,28,44,0.1)]"
                      : "text-erp-muted hover:bg-white/70 hover:text-erp-ink",
                  ].join(" ")}
                  onClick={() => handleOperationTypeChange("cashIn")}
                >
                  <AddCircleOutlineIcon className="!text-[18px] text-erp-success" />
                  Cash In
                </button>
                <button
                  type="button"
                  className={[
                    "inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-[13px] font-semibold transition-all",
                    formData.operationType === "cashOut"
                      ? "border border-erp-line bg-white text-erp-ink shadow-[0_2px_8px_rgba(11,28,44,0.1)]"
                      : "text-erp-muted hover:bg-white/70 hover:text-erp-ink",
                  ].join(" ")}
                  onClick={() => handleOperationTypeChange("cashOut")}
                >
                  <RemoveCircleOutlineIcon className="!text-[18px] text-erp-danger" />
                  Cash Out
                </button>
              </div>

              <form onSubmit={wrap(handleSubmit)} className="flex flex-col gap-4">
                <div className="flex flex-col gap-4">
                  <InputText
                    required={formData.operationType === "cashIn"}
                    name="amount"
                    label={
                      formData.operationType === "cashIn"
                        ? "Cash In Amount"
                        : "Cash Out Amount"
                    }
                    type="number"
                    placeholder="Enter amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    onFocus={(e) => {
                      dispatch(
                        posInputFocus({
                          name: "data",
                          value: e.target.name,
                        }),
                      );
                    }}
                  />

                  <InputText
                    name="reason"
                    label="Reason"
                    type="text"
                    placeholder="Enter reason for this operation"
                    value={formData.reason}
                    onChange={handleInputChange}
                    onFocus={(e) => {
                      dispatch(
                        posInputFocus({ name: "data", value: e.target.name }),
                      );
                    }}
                  />
                </div>

                <div className="flex justify-end gap-2.5 border-t border-erp-line pt-4">
                  <button
                    type="button"
                    className={btnGhost}
                    onClick={isCloseMain}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={btnPrimary}
                    disabled={submitting}
                  >
                    {submitting
                      ? "Processing..."
                      : `Record ${
                          formData.operationType === "cashIn"
                            ? "Cash In"
                            : "Cash Out"
                        }`}
                  </button>
                </div>
              </form>
            </>
          )}

          {currentView === "status" && (
            <div>
              {statusLoading ? (
                <div className="flex min-h-[160px] items-center justify-center text-[13px] font-medium text-erp-muted">
                  Loading POS status...
                </div>
              ) : posStatus ? (
                <div className="flex flex-col gap-4">
                  <div className="rounded-xl border border-erp-line bg-erp-surface p-4">
                    <h3 className="m-0 mb-3 text-[12px] font-bold uppercase tracking-[0.06em] text-erp-ink">
                      Current POS Session
                    </h3>

                    <div className="mb-4 grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-erp-muted">
                          Status
                        </span>
                        <span
                          className={[
                            "inline-flex w-fit rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide",
                            posStatus.isOpen
                              ? "bg-erp-success/15 text-erp-success"
                              : "bg-erp-danger/15 text-erp-danger",
                          ].join(" ")}
                        >
                          {posStatus.isOpen ? "Open" : "Closed"}
                        </span>
                      </div>

                      {posStatus.isOpen ? (
                        <>
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-erp-muted">
                              Cash In
                            </span>
                            <span className="text-[14px] font-bold tabular-nums text-erp-success">
                              {currencySymbol}
                              {Number(posStatus.cashIn).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-erp-muted">
                              Cash Out
                            </span>
                            <span className="text-[14px] font-bold tabular-nums text-erp-danger">
                              {currencySymbol}
                              {Number(posStatus.cashOut).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-erp-muted">
                              Drawer Balance
                            </span>
                            <span
                              className={[
                                "text-[14px] font-bold tabular-nums",
                                posStatus.currentBalance >= 0
                                  ? "text-erp-success"
                                  : "text-erp-danger",
                              ].join(" ")}
                            >
                              {currencySymbol}
                              {Number(posStatus.currentBalance).toFixed(2)}
                            </span>
                          </div>
                          <div className="col-span-2 flex flex-col gap-1 rounded-lg border border-erp-line bg-white px-3 py-2">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-erp-muted">
                              Session Period
                            </span>
                            <span className="text-[12px] font-medium text-erp-ink">
                              Start:{" "}
                              {posHistory.length > 0
                                ? formatDateTime(
                                    posHistory[posHistory.length - 1]
                                      .createdAt || "",
                                  )
                                : "N/A"}
                            </span>
                            <span className="text-[12px] font-medium text-erp-ink">
                              End: {formatDateTime(currentTime)}
                            </span>
                          </div>
                        </>
                      ) : null}
                    </div>

                    {posStatus.isOpen ? (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="inline-flex h-9 items-center justify-center rounded-lg border border-erp-danger/30 bg-erp-danger px-3 text-[12px] font-bold text-white transition-colors hover:bg-[#9a1e14]"
                          onClick={handleCloseSession}
                        >
                          Close Registry
                        </button>
                        <button
                          type="button"
                          className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-erp-accent/30 bg-erp-accent px-3 text-[12px] font-bold text-white transition-colors hover:bg-erp-accent-bright"
                          onClick={() => {
                            loadPosHistory().catch(() => {});
                            setPrintOpen(true);
                          }}
                        >
                          <PrintIcon className="!text-[16px]" />
                          Print Cash Mgmt
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {posStatus.isOpen ? (
                    <div className="rounded-xl border border-erp-line bg-white p-4">
                      <h4 className="m-0 mb-2 text-[12px] font-bold uppercase tracking-[0.05em] text-erp-ink">
                        Cash Flow Information
                      </h4>
                      <p className="m-0 mb-2 text-[12px] leading-relaxed text-erp-muted">
                        Cash balance includes all cash transactions:
                      </p>
                      <ul className="m-0 list-none space-y-1 p-0 text-[12px] text-erp-muted">
                        <li>
                          • Cash received from customers increases the balance
                        </li>
                        <li>
                          • Change given to customers decreases the balance
                        </li>
                        <li>
                          • Manual cash in/out operations affect the balance
                        </li>
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="flex min-h-[160px] items-center justify-center text-[13px] font-medium text-erp-muted">
                  No POS session data available
                </div>
              )}
            </div>
          )}

          {currentView === "history" && (
            <div>
              {historyLoading ? (
                <div className="flex min-h-[160px] items-center justify-center text-[13px] font-medium text-erp-muted">
                  Loading history...
                </div>
              ) : posHistory.length > 0 ? (
                <div>
                  <h3 className="m-0 mb-3 text-[12px] font-bold uppercase tracking-[0.06em] text-erp-ink">
                    Cash Operations History
                  </h3>
                  <div className="flex flex-col gap-2.5">
                    {posHistory.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between gap-3 rounded-xl border border-erp-line bg-white px-3.5 py-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="mb-1 flex flex-wrap items-center gap-2">
                            <span className="text-[13px] font-bold tabular-nums text-erp-success">
                              +{currencySymbol}
                              {Number(item.cashIn).toFixed(2)}
                            </span>
                            <span className="text-[13px] font-bold tabular-nums text-erp-danger">
                              -{currencySymbol}
                              {Number(item.cashOut).toFixed(2)}
                            </span>
                          </div>
                          <p className="m-0 truncate text-[12px] font-medium text-erp-ink">
                            {item.reason || "No reason provided"}
                          </p>
                          <p className="m-0 mt-0.5 text-[11px] text-erp-muted">
                            {formatDateTime(item.createdAt)}
                          </p>
                        </div>
                        <span
                          className={[
                            "shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                            !item.active
                              ? "bg-erp-danger/15 text-erp-danger"
                              : "bg-erp-success/15 text-erp-success",
                          ].join(" ")}
                        >
                          {!item.active ? "Closed" : "Open"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex min-h-[160px] items-center justify-center text-[13px] font-medium text-erp-muted">
                  No transaction history available
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Close session popup */}
      {showClosePopup ? (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center bg-erp-navy/60 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-[440px] overflow-hidden rounded-2xl border border-erp-line bg-white shadow-[0_24px_60px_rgba(11,28,44,0.3)]">
            <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-gradient-to-r from-erp-navy via-erp-navy-mid to-erp-navy-soft px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-erp-gold/40 bg-erp-gold/20 text-erp-gold">
                  <AccountBalanceWalletIcon className="!text-[20px]" />
                </div>
                <div>
                  <h2 className="m-0 text-[15px] font-bold text-white">
                    Close POS Session
                  </h2>
                  <p className="m-0 mt-0.5 text-[11px] text-white/60">
                    Count drawer cash and confirm
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="inline-flex rounded-lg border border-white/15 bg-white/10 p-1.5 text-white/85 transition-colors hover:bg-white/20 hover:text-white disabled:opacity-50"
                onClick={() => setShowClosePopup(false)}
                disabled={closeSessionLoading}
              >
                <CloseIcon className="!text-[18px]" />
              </button>
            </div>

            <div className="p-5">
              <div className="mb-4 rounded-xl border border-erp-line bg-erp-surface px-3.5 py-3">
                <h3 className="m-0 text-[14px] font-bold text-erp-ink">
                  Expected Cash Balance: {currencySymbol}
                  {Number(posStatus?.currentBalance).toFixed(2)}
                </h3>
                <p className="m-0 mt-1 text-[12px] leading-relaxed text-erp-muted">
                  Please count the actual cash in the drawer and enter the
                  amount below.
                </p>
              </div>

              <form
                onSubmit={handleCloseSessionSubmit}
                className="flex flex-col gap-4"
              >
                <InputText
                  required={true}
                  name="actualCashAmount"
                  label="Actual Cash Amount in Drawer"
                  type="number"
                  placeholder="Count and enter actual cash amount"
                  value={closeSessionData.actualCashAmount}
                  onChange={handleClosePopupInputChange}
                />

                <InputText
                  required={discrepancyValue !== 0}
                  name="discrepancyReason"
                  label={`Reason for Discrepancy ${
                    discrepancyValue !== 0 ? "(Required)" : "(Optional)"
                  }`}
                  type="text"
                  placeholder="Explain the reason for cash difference"
                  value={closeSessionData.discrepancyReason}
                  onChange={handleClosePopupInputChange}
                />

                {closeSessionData.actualCashAmount ? (
                  <div
                    className={[
                      "rounded-xl border px-3.5 py-3",
                      discrepancyValue === 0
                        ? "border-erp-success/30 bg-erp-success/10"
                        : discrepancyValue > 0
                          ? "border-erp-accent/30 bg-erp-accent/10"
                          : "border-erp-danger/30 bg-erp-danger/10",
                    ].join(" ")}
                  >
                    <div
                      className={[
                        "text-[13px] font-bold tabular-nums",
                        discrepancyValue === 0
                          ? "text-erp-success"
                          : discrepancyValue > 0
                            ? "text-erp-accent"
                            : "text-erp-danger",
                      ].join(" ")}
                    >
                      Discrepancy: {currencySymbol}
                      {discrepancyValue.toFixed(2)}
                    </div>

                    {discrepancyValue !== 0 ? (
                      <p className="m-0 mt-1.5 text-[12px] leading-relaxed text-erp-muted">
                        {discrepancyValue > 0
                          ? `System will add ${currencySymbol}${discrepancyValue.toFixed(2)} to Cash In (surplus found)`
                          : `System will add ${currencySymbol}${Math.abs(discrepancyValue).toFixed(2)} to Cash Out (deficit found)`}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <div className="flex justify-end gap-2.5 border-t border-erp-line pt-4">
                  <button
                    type="button"
                    className={btnGhost}
                    onClick={() => setShowClosePopup(false)}
                    disabled={closeSessionLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={btnPrimary}
                    disabled={closeSessionLoading}
                  >
                    {closeSessionLoading
                      ? "Closing..."
                      : "Close Session & Adjust"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      <CashManagementPrint
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        posStatus={posStatus}
        posHistory={posHistory}
      />
    </div>
  );
};

export default PosModel;
